import { Injectable, inject } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { CloudLogsService, LogType } from 'src/app/shared/services/cloud-logs.service';
import html2canvas from 'html2canvas-pro';

type CapturePath = 'html2canvas' | 'failed';

/** Funciones CSS Color Level 4 que rompen parsers antiguos / Safari residual. */
const MODERN_COLOR_FN_PATTERN =
	/\b(?:color|oklch|oklab|lab|lch|color-mix)\s*\(/i;

const COLOR_STYLE_PROPERTIES = [
	'color',
	'background-color',
	'border-top-color',
	'border-right-color',
	'border-bottom-color',
	'border-left-color',
	'outline-color',
	'fill',
	'stroke',
	'-webkit-text-fill-color',
] as const;

@Injectable({
	providedIn: 'root',
})
export class ScreenshotService {
	private subscription?: Subscription;
	private contador: number = 1;
	private cloudLogsService = inject(CloudLogsService);
	private static readonly SCREENSHOT_INTERVAL_MS = 10000;
	private static readonly ERROR_STACK_MAX_CHARS = 500;
	private static readonly GENERIC_CAPTURE_ERROR = 'Error al tomar la captura';

	public saveScreenshot(info: any, type: any): void {
		this.captureWithHtml2Canvas()
			.then((dataUrl) => {
				this.cloudLogsService.sendCloudLogs(
					{
						...info,
						capturePath: 'html2canvas' satisfies CapturePath,
						userAgent: this.getUserAgentSnippet(),
					},
					type,
					LogType.Info,
					dataUrl,
				);
			})
			.catch((error: unknown) => {
				this.cloudLogsService.sendCloudLogs(
					{
						...info,
						...this.buildCaptureFailurePayload(error),
					},
					type,
					LogType.Error,
				);
			});
	}

	public startSaveScreenshot(type: any): void {
		if (this.subscription && !this.subscription.closed) {
			return;
		}
		this.saveScreenshot({ numScreenShot: this.contador }, type);
		this.subscription = interval(ScreenshotService.SCREENSHOT_INTERVAL_MS).subscribe(() => {
			this.contador++;
			this.saveScreenshot({ numScreenShot: this.contador }, type);
		});
	}

	public stopSaveScreenshot(): void {
		if (this.subscription) {
			this.subscription.unsubscribe();
			this.subscription = undefined;
		}
	}

	private async captureWithHtml2Canvas(): Promise<string> {
		const canvas = await html2canvas(document?.body, this.buildHtml2CanvasOptions());
		return canvas.toDataURL('image/jpeg', 0.8);
	}

	private buildHtml2CanvasOptions(): {
		useCORS: boolean;
		logging: boolean;
		onclone: (clonedDoc: Document) => void;
	} {
		return {
			useCORS: true,
			logging: false,
			onclone: (clonedDoc: Document) => {
				this.sanitizeCloneStyles(clonedDoc);
			},
		};
	}

	/**
	 * Normaliza colores modernos en el DOM clonado para que html2canvas no pete
	 * con `color()` / oklch / etc. (Safari/iOS residual tras NIF-998).
	 */
	private sanitizeCloneStyles(clonedDoc: Document): void {
		this.rewriteStyleTagsInClone(clonedDoc);
		this.normalizeElementColorsInClone(clonedDoc);
	}

	private rewriteStyleTagsInClone(clonedDoc: Document): void {
		const styleNodes = clonedDoc.querySelectorAll('style');
		styleNodes.forEach((styleEl) => {
			const cssText = styleEl.textContent ?? '';
			if (!MODERN_COLOR_FN_PATTERN.test(cssText)) {
				return;
			}
			styleEl.textContent = this.replaceModernColorFunctions(cssText);
		});
	}

	private normalizeElementColorsInClone(clonedDoc: Document): void {
		const liveRoot = typeof document !== 'undefined' ? document.body : null;
		const cloneRoot = clonedDoc.body;
		if (!liveRoot || !cloneRoot) {
			return;
		}

		const liveElements = [liveRoot, ...Array.from(liveRoot.querySelectorAll<HTMLElement>('*'))];
		const cloneElements = [cloneRoot, ...Array.from(cloneRoot.querySelectorAll<HTMLElement>('*'))];
		const pairCount = Math.min(liveElements.length, cloneElements.length);

		for (let i = 0; i < pairCount; i++) {
			const liveEl = liveElements[i];
			const cloneEl = cloneElements[i];
			if (!(liveEl instanceof HTMLElement) || !(cloneEl instanceof HTMLElement)) {
				continue;
			}

			const inlineStyle = cloneEl.getAttribute('style');
			if (inlineStyle && MODERN_COLOR_FN_PATTERN.test(inlineStyle)) {
				cloneEl.setAttribute('style', this.replaceModernColorFunctions(inlineStyle));
			}

			let computed: CSSStyleDeclaration;
			try {
				computed = getComputedStyle(liveEl);
			} catch {
				continue;
			}

			for (const prop of COLOR_STYLE_PROPERTIES) {
				const value = computed.getPropertyValue(prop);
				if (!value || !MODERN_COLOR_FN_PATTERN.test(value)) {
					continue;
				}
				const rgba = this.cssColorToRgba(value);
				if (rgba) {
					cloneEl.style.setProperty(prop, rgba);
				}
			}
		}
	}

	/** Sustituye llamadas a funciones de color modernas por transparent. */
	private replaceModernColorFunctions(cssText: string): string {
		return cssText.replace(
			/\b(?:color|oklch|oklab|lab|lch|color-mix)\s*\((?:[^()]|\([^()]*\))*\)/gi,
			'transparent',
		);
	}

	/**
	 * Convierte cualquier color CSS válido a rgba() vía canvas 1×1 (sRGB),
	 * útil cuando Safari expone `color(display-p3 …)` en computed styles.
	 */
	private cssColorToRgba(cssColor: string): string | null {
		try {
			const canvas = document.createElement('canvas');
			canvas.width = 1;
			canvas.height = 1;
			const ctx = canvas.getContext('2d', { colorSpace: 'srgb' } as CanvasRenderingContext2DSettings);
			if (!ctx) {
				return null;
			}
			ctx.fillStyle = '#000';
			ctx.fillStyle = cssColor;
			ctx.fillRect(0, 0, 1, 1);
			const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
			return `rgba(${r}, ${g}, ${b}, ${a / 255})`;
		} catch {
			return null;
		}
	}

	private buildCaptureFailurePayload(error: unknown): Record<string, unknown> {
		return {
			capturePath: 'failed' satisfies CapturePath,
			userAgent: this.getUserAgentSnippet(),
			...this.serializeErrorFields(error),
		};
	}

	private serializeErrorFields(error: unknown): Record<string, unknown> {
		const message = this.resolveErrorMessage(error);
		const name = error instanceof Error ? error.name : typeof error;
		const stack =
			error instanceof Error && typeof error.stack === 'string'
				? error.stack.slice(0, ScreenshotService.ERROR_STACK_MAX_CHARS)
				: undefined;

		return {
			errorSaveScreenShot: message,
			errorName: name,
			...(stack ? { errorStack: stack } : {}),
		};
	}

	private resolveErrorMessage(error: unknown): string {
		if (error instanceof Error && error.message) {
			return error.message;
		}
		if (typeof error === 'string' && error.trim()) {
			return error;
		}
		return ScreenshotService.GENERIC_CAPTURE_ERROR;
	}

	private getUserAgentSnippet(): string {
		try {
			return typeof navigator !== 'undefined' ? navigator.userAgent : '';
		} catch {
			return '';
		}
	}
}
